import React, { useState } from 'react';
import styles from './LookalikeTable.module.scss';
import type { LookalikeRow } from '../../../store/investor/apiHelpers';
import clsx from 'clsx';
import { STATUS_COLORS, COLUMNS, OVERVIEW_HOVER_CARD } from './constants';
import { useLookalikeTable } from './useLookalikeTable';
import type { StatusOption } from '../../../modules/Dashboard/DashboardModules/Overview/components/RelatedCompaniesNetwork/types';
import { purpleOfficeIcon, purpleRedirectIcon } from '../../../assets/hostedassets';
import Tooltip from '../../../common/_custom/Tooltip/Tooltip';
import SwapVertIcon from '../../../assets/swap_vert.svg';
import SwapVertUpIcon from '../../../assets/swap_vert_up.svg';
import SwapVertDownIcon from '../../../assets/swap_vert_down.svg';

interface LookalikeTableProps {
    source: LookalikeRow;
    lookalikes: LookalikeRow[];
    onRowClick: (orgId: string) => void;
    statusOptions?: StatusOption[];
    relationshipStatusOptions?: StatusOption[];
}

// Tooltip sets `overflow: hidden` inline on .Tooltip-Tip — inline styles can only be overridden
// by inline styles. Everything else (width, padding, shadow, etc.) lives in the module SCSS.
const OVERVIEW_TIP_OVERFLOW_OVERRIDE: React.CSSProperties = { overflowX: 'visible', overflowY: 'visible' };

interface EntityPillProps {
    orgName: string;
    isSource?: boolean;
    onClick?: () => void;
}

const EntityPill: React.FC<EntityPillProps> = ({ orgName, isSource = false, onClick }) => {
    const [hovered, setHovered] = useState(false);

    if (!orgName) return <span className={styles.naText}>NA</span>;

    const icon = isSource ? purpleOfficeIcon : hovered ? purpleRedirectIcon : purpleOfficeIcon;
    const iconStyle = isSource ? { filter: 'brightness(0.3)' } : undefined;
    const interactive = !isSource && !!onClick;

    const pill = (
        <div
            className={clsx(styles.entityCell, isSource && styles.sourceEntityCell, interactive && styles.entityCellInteractive)}
            onMouseEnter={isSource ? undefined : () => setHovered(true)}
            onMouseLeave={isSource ? undefined : () => setHovered(false)}
            onClick={interactive ? onClick : undefined}
            onKeyDown={
                interactive
                    ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onClick?.();
                          }
                      }
                    : undefined
            }
            tabIndex={interactive ? 0 : undefined}
            role={interactive ? 'button' : undefined}
            aria-label={interactive ? `View ${orgName} details` : undefined}
        >
            <img src={icon} alt="" className={styles.entityIcon} style={iconStyle} />
            <span className={isSource ? styles.sourceEntityName : styles.entityName}>{orgName}</span>
        </div>
    );

    return (
        <Tooltip
            content={orgName}
            direction="top"
            usePortal
            fitContent
            // When the pill is interactive (lookalike row), it's wrapped by the overview Tooltip.
            // Both must skip the mutual-close dispatch so they coexist on the same trigger.
            disablePortalShowDispatch={interactive}
        >
            {pill}
        </Tooltip>
    );
};

const SortIcon: React.FC<{ direction?: 'asc' | 'desc' | null }> = ({ direction }) => {
    const isActive = !!direction;
    const src = direction === 'asc' ? SwapVertUpIcon : direction === 'desc' ? SwapVertDownIcon : SwapVertIcon;
    const alt = direction === 'asc' ? 'Sort ascending' : direction === 'desc' ? 'Sort descending' : 'Sort';
    const size = isActive ? 10 : 18;
    return (
        <span className={clsx(styles.sortIconWrap, isActive && styles.sortIconActive)}>
            <img src={src} alt={alt} width={size} height={size} />
        </span>
    );
};

const LookalikeTable: React.FC<LookalikeTableProps> = ({
    source,
    lookalikes,
    onRowClick,
    statusOptions = [],
    relationshipStatusOptions = [],
}) => {
    const { sortKey, sortDir, handleSort, sortedLookalikes, formatInvestors, getCompanyOverview, fetchCompanyOverview } =
        useLookalikeTable(lookalikes);

    const resolveLabel = (status: string | null, options: StatusOption[]): { label: string; bg?: string; color?: string } | null => {
        if (!status) return null;
        const match = options.find((opt) => opt.value?.toLowerCase() === status.toLowerCase());
        if (match) return { label: match.label, bg: match.backgroundColor, color: match.labelColor };
        return { label: status };
    };

    const renderStatus = (status: string | null, options: StatusOption[] = []) => {
        if (!status) return <span className={styles.naText}>NA</span>;
        const resolved = resolveLabel(status, options);
        const label = resolved?.label || status;
        const key = label.toLowerCase().trim();
        const fallbackStyle = STATUS_COLORS[key] || STATUS_COLORS[status.toLowerCase().trim()] || { bg: '#f3f4f6', color: '#374151' };
        const style = resolved?.bg ? { bg: resolved.bg, color: resolved.color || '#374151' } : fallbackStyle;
        return (
            <span className={styles.statusPill} style={{ backgroundColor: style.bg, color: style.color }}>
                {label}
            </span>
        );
    };

    const na = <span className={styles.naText}>NA</span>;
    const formatCurrency = (value: number | null) => (value != null ? `₹${Math.round(value)} Cr` : na);

    const renderCellValue = (row: LookalikeRow, key: string, isSource = false): React.ReactNode => {
        switch (key) {
            case 'similarityScore':
                if (isSource) return <span className={styles.naText}>—</span>;
                return <span className={styles.scoreCell}>{(row.similarityScore * 100).toFixed(0)}%</span>;
            case 'orgName': {
                if (isSource || !row.orgId) return <EntityPill orgName={row.orgName} isSource={isSource} />;
                const entry = getCompanyOverview(row.orgId);
                const trimmed = entry?.content?.trim();
                const hasContent = !!trimmed;
                const bodyText =
                    entry?.loading && !hasContent ? OVERVIEW_HOVER_CARD.LOADING : hasContent ? trimmed : OVERVIEW_HOVER_CARD.EMPTY;
                return (
                    <Tooltip
                        direction="right"
                        usePortal
                        portalAnchor="right"
                        hideArrow
                        hideDelay={200}
                        // Lets the inner EntityPill name tooltip coexist on the same trigger.
                        disablePortalShowDispatch
                        onShow={() => fetchCompanyOverview(row.orgId)}
                        tipClassName={styles.OverviewTooltipTip}
                        contentClassName={styles.OverviewTooltipContent}
                        style={OVERVIEW_TIP_OVERFLOW_OVERRIDE}
                        content={
                            <div className={styles.OverviewTooltip}>
                                <div className={styles.OverviewTooltipHeader}>{OVERVIEW_HOVER_CARD.TITLE}</div>
                                <div className={clsx(styles.OverviewTooltipBody, !hasContent && styles.OverviewTooltipMuted)}>
                                    {bodyText}
                                </div>
                            </div>
                        }
                    >
                        <EntityPill orgName={row.orgName} onClick={() => onRowClick(row.orgId)} />
                    </Tooltip>
                );
            }
            case 'leadStatus':
                return renderStatus(row.leadStatus, statusOptions);
            case 'relationshipStatus':
                return renderStatus(row.relationshipStatus, relationshipStatusOptions);
            case 'revenue':
                return formatCurrency(row.revenue);
            case 'ebitdaPct':
                return row.ebitdaPct != null ? `${Math.round(row.ebitdaPct)}%` : na;
            case 'existingDebt':
                return formatCurrency(row.existingDebt);
            case 'bureauScore':
                return row.bureauScore != null && row.bureauScore !== '' ? row.bureauScore : na;
            case 'equityRaised':
                return formatCurrency(row.equityRaised);
            case 'investors':
                return formatInvestors(row.investors);
            case 'sector':
                return row.sector ? row.sector : na;
            case 'subSector':
                return row.subSector ? row.subSector : na;
            case 'state':
                return row.state ? <span className={styles.stateCell}>{row.state}</span> : na;
            default:
                return (row as any)[key] || na;
        }
    };

    return (
        <div className={styles.tableContainer}>
            <table className={styles.lookalikeTable}>
                <thead>
                    <tr>
                        {COLUMNS.map((col) => (
                            <th
                                key={col.key}
                                style={{ width: col.width }}
                                className={clsx(
                                    styles.sortableHeader,
                                    col.numeric && styles.numericHeader,
                                    col.key === 'orgName' && styles.stickyCol,
                                    col.key === 'state' && styles.stateCol,
                                )}
                                aria-sort={
                                    col.sortable && sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                                }
                            >
                                <button type="button" className={styles.sortButton} onClick={() => handleSort(col.key)}>
                                    <span className={styles.headerContent}>
                                        {col.label}
                                        <SortIcon direction={sortKey === col.key ? sortDir : null} />
                                    </span>
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {/* Source company - fixed at top */}
                    <tr className={styles.sourceRow}>
                        {COLUMNS.map((col) => (
                            <td
                                key={col.key}
                                className={clsx(
                                    col.numeric && styles.numericCell,
                                    col.key === 'orgName' && styles.stickyCol,
                                    col.key === 'state' && styles.stateCol,
                                )}
                            >
                                {renderCellValue(source, col.key, true)}
                            </td>
                        ))}
                    </tr>

                    {/* Lookalike rows */}
                    {sortedLookalikes.map((row) => (
                        <tr key={row.orgId} className={styles.lookalikeRow}>
                            {COLUMNS.map((col) => (
                                <td
                                    key={col.key}
                                    className={clsx(
                                        col.numeric && styles.numericCell,
                                        col.key === 'orgName' && styles.stickyCol,
                                        col.key === 'state' && styles.stateCol,
                                    )}
                                >
                                    {renderCellValue(row, col.key)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default LookalikeTable;
